import React, { useEffect, useState } from 'react';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from '@components/atoms/head/head';
import { ContentStackPageNames } from '@enums/contentStack';
import ProductSearch, { ProductSearchProps } from '@components/catalog/product-search/product-search';
import { getPageMeta } from '@constants/metas/default';
import ContentStackService from '@services/content-stack-service';
import extractFromContext from '@helpers/utils/extract-from-context';
import { parseURLQueryParameters } from '@helpers/utils/catalog/catalog-url-helpers';
import { useAnalytics } from '@context/analytics.context';
import useContentStackLivepreview from '@hooks/use-contentstack-livepreview';
import { usePreferences } from '@context/preferences.context';
import RequestError from '@errors/request-error';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import DefaultLayout from '@layouts/default-layout';
import logger from '@helpers/utils/logger/client';
import { Deal } from '@interfaces/models/deal';
import { ActiveDealQueryKeys, MarketingFiltersQueryKeys } from '@enums/react-query-keys';
import { getActiveDeal } from '@api/deals';
import { ContentsMarketingFilter } from '@interfaces/models/contentsMarketingFilter';
import { getMarketingFilters } from '@api/contents';
import { dehydrate, DehydratedState, QueryClient, useHydrate } from '@tanstack/react-query';
import useSearchSessionId from '@hooks/catalog-context/use-search-session-id';
import { RESET_CATALOG_DATALAYER } from '@helpers/utils/catalog/catalog-tracking-utils';
import { getAppInitialProps } from '@helpers/initial-props';
import useCatalogVoucherBanner from '@hooks/catalog-context/use-catalog-voucher-banner';
import CatalogVoucherBanner from '@components/catalog/catalog-voucher-banner/catalog-voucher-banner';
import { SSRConfig } from 'next-i18next';
import { Campaign, CampaignBlock } from '@interfaces/models/campaign';

export type CampaignPageProps = {
  dehydratedState: DehydratedState;
  catalogContextProps: ProductSearchProps['catalogContextProps'];
  campaignContextProps: ProductSearchProps['campaignContextProps'];
};

const CampaignPage: NextPageWithLayout<CampaignPageProps> = (props): React.JSX.Element => {
  const { campaignContextProps, catalogContextProps, dehydratedState } = props;
  const [searchSessionIdState, setSearchSessionIdState] = React.useState<string>('');
  const { showBanner, text: voucherBannerText } = useCatalogVoucherBanner();

  useHydrate(dehydratedState);

  const { language } = usePreferences();
  const { updateDataLayer } = useAnalytics();
  const [campaignPageContext, setCampaignPageContext] = useState(campaignContextProps?.campaign);
  const { addLivePreviewEventListener, removeLivePreviewEventListener } =
    useContentStackLivepreview(setCampaignPageContext);
  const pageMetas = getPageMeta(campaignPageContext?.seo, language, ContentStackPageNames.CAMPAIGN_PAGE, {
    title: campaignPageContext?.displayTitle,
    description: campaignPageContext?.subtitle,
  });

  const { assignSearchSessionId } = useSearchSessionId();

  useEffect(() => {
    const searchSessionId = assignSearchSessionId();
    setSearchSessionIdState(searchSessionId);

    updateDataLayer({
      ...RESET_CATALOG_DATALAYER,
      screen_name: '/search_results/catalog',
      env_template: 'catalog',
      screen_category: 'search',
      screen_subcategory: 'search_results',
      filtered_campaign_id: campaignPageContext?.uid,
      onsite_campaign_id: campaignPageContext?.campaign_id,
      campaign_id: campaignPageContext?.campaign_id,
      search_session_id: searchSessionId,
    });

    addLivePreviewEventListener();
    return () => removeLivePreviewEventListener();
  }, []);

  return (
    <DefaultLayout shouldShowBanner={showBanner}>
      {showBanner && <CatalogVoucherBanner text={voucherBannerText} />}
      <Head metadata={pageMetas} />
      <ProductSearch
        catalogContextProps={{ ...catalogContextProps, searchSessionId: searchSessionIdState }}
        campaignContextProps={{ campaign: campaignPageContext ?? null }}
      />
    </DefaultLayout>
  );
};

CampaignPage.Layout = [];
CampaignPage.PageType = 'CampaignPage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { resolvedUrl } = context;
  const { locale, preferences, userId, serverAxiosRequestData } = extractFromContext(context);
  const { page, query, sortBy, campaignId } = parseURLQueryParameters(resolvedUrl);
  const queryClient = new QueryClient();

  try {
    const initialProps = await getAppInitialProps(context, queryClient);

    const mandatoryRequests = [
      serverSideTranslations(locale, ['default-layout', 'vc-authentication', 'a11y', 'common', 'saved-searches']),
      ContentStackService.getDataForCMSPage(
        ContentStackPageNames.CAMPAIGN_PAGE,
        { ...preferences, userId },
        { campaignId },
      ),
      queryClient.fetchQuery<Deal>({
        queryKey: [ActiveDealQueryKeys.ACTIVE_DEAL],
        queryFn: () => getActiveDeal(serverAxiosRequestData),
      }),
      queryClient.fetchQuery<ContentsMarketingFilter[]>({
        queryKey: [MarketingFiltersQueryKeys.MARKETING_FILTERS],
        queryFn: () => getMarketingFilters(serverAxiosRequestData),
      }),
    ];

    const promiseResults = await Promise.allSettled(mandatoryRequests);

    const [translations, campaign] = promiseResults.map((promiseResult, index) => {
      if (promiseResult.status === 'fulfilled') {
        return promiseResult.value;
      }

      // Throw on error for tracking to pick it up for required requests, the rest can fail
      if (index < mandatoryRequests.length) {
        throw new RequestError(promiseResult.reason);
      }

      return null;
    }) as [SSRConfig, Campaign & CampaignBlock, Deal, ContentsMarketingFilter[]];

    return {
      props: {
        ...initialProps,
        ...translations,
        dehydratedState: dehydrate(queryClient),
        catalogContextProps: {
          metadata: null,
          applicableSizeIds: [1, 2],
          filters: null,
          page,
          sortBy,
          query,
          products: [],
          facets: null,
          priceBound: {
            lower: 0,
            upper: 0,
          },
        },
        campaignContextProps: {
          campaign: campaign ?? null,
        },
      },
    };
  } catch (e) {
    // Axios 404 errors should get redirected straight to error page
    if (isRequestError(e)) {
      if (!isRequest404Error(e)) {
        logger.error(
          e,
          'There was an api request error on the Campaign Page - getServerSideProps - [[...campaignSlug]].page.tsx',
        );
      }
      return {
        notFound: true,
      };
    }
    // Other errors will get logged
    logger.error(
      e,
      'The server failed to render the Campaign Page - getServerSideProps - [[...campaignSlug]].page.tsx',
    );
    throw e;
  }
};

export default CampaignPage;
